import React, { useState } from "react";
import { MdArrowBack, MdOutlineBackspace } from "react-icons/md";
import { BiInfoCircle } from "react-icons/bi";
import { Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import { differenceInDays, format } from "date-fns";
import { useTranslation } from "react-i18next";
import useDesktopSize from "../../../../../../utils/hooks/useDesktopSize";
import useLocaleByLanguage from "../../../../../../utils/hooks/useLocaleByLanguage";
import { useOtherAccountDisplayName } from "../../../../../../utils/hooks/useOtherAccountDisplayName";
import useCourtPasses from "../../../hooks/useCourtPasses";
import EAButton from "../../../../shared/Buttons/EAButton";
import EAAlert from "../../../../shared/EAAlert";
import EAChip from "../../../../shared/Chip/EAChip";
import EAModal from "../../../../shared/Modal/EAModal";
import EASkeletonLoader from "../../../../shared/EASkeletonLoader";
import { Exercise } from "../../../../../models/Exercise";
import { Ticket } from "../../../../../models/Ticket";
import { calculateRemainingEntrances, isOpenPass, isValidityEndingSoon } from "../../../../../utils/TicketPassHelper";
import "./PassSelection.scss";
import getId from "../../../../../../utils/getId";
import usePassEligibility from "./usePassEligibility";

const HtmlTooltip = styled(({ className, ...props }: any) => <Tooltip {...props} classes={{ popper: className }} />)(
  ({ theme }) => ({
    [`& .MuiTooltip-tooltip`]: {
      backgroundColor: "#f5f5f9",
      color: "rgba(0, 0, 0, 0.87)",
      maxWidth: 300,
      fontSize: theme.typography.pxToRem(12),
      border: "1px solid #dadde9",
    },
  }),
);

interface IProps {
  exerciseDetails: Exercise | null;
  onPassSelected: (passId: string, assigned?: boolean) => void;
  onBack: () => void;
  alert?: string;
  loading?: boolean;
}

const isUnlimited = (pass: Ticket): boolean => isOpenPass(pass.passtype) || pass.entrances > 200;

const expiryLabel = (pass: Ticket): string => {
  const daysUntilExpiration = differenceInDays(pass.validto, new Date());
  if (daysUntilExpiration <= 0) return "Traci ważność";
  if (daysUntilExpiration === 1) return "Wygasa za 1 dzień";
  return `Wygasa za ${daysUntilExpiration} dni`;
};

const PassValidity: React.FC<{ pass: Ticket }> = ({ pass }) => (
  <p className="pass-card__meta">
    Ważny od {format(pass.validfrom, "dd.MM.yyyy")} do {format(pass.validto, "dd.MM.yyyy")}
  </p>
);

const PassEntrances: React.FC<{ pass: Ticket }> = ({ pass }) => {
  const remaining = calculateRemainingEntrances(pass);
  const unlimited = isUnlimited(pass);
  const percent = unlimited || pass.entrances <= 0 ? 0 : Math.min(100, Math.max(0, (remaining / pass.entrances) * 100));

  return (
    <span className="pass-card__entrances">
      Pozostało wejść:{" "}
      {unlimited ? (
        <strong>bez limitu</strong>
      ) : (
        <strong>
          {remaining} z {pass.entrances}
        </strong>
      )}
      {!unlimited && (
        <svg
          className="pass-card__bar"
          width="48"
          height="2"
          viewBox="0 0 48 2"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={pass.entrances}
          aria-valuenow={remaining}
          aria-label="Pozostałe wejścia"
        >
          <rect className="pass-card__bar-track" x="0" y="0" width="48" height="2" />
          <rect className="pass-card__bar-fill" x="0" y="0" width={(percent / 100) * 48} height="2" />
        </svg>
      )}
    </span>
  );
};

const isPassExhausted = (pass: Ticket): boolean => !isUnlimited(pass) && calculateRemainingEntrances(pass) <= 0;

const PassCard: React.FC<{
  pass: Ticket;
  isAssigned: boolean;
  isLoading: boolean;
  suggested: boolean;
  exhausted: boolean;
  error?: string;
  onPassSelected: (passId: string, assigned?: boolean) => void;
  onShowDescription: (pass: Ticket) => void;
}> = ({ pass, isAssigned, isLoading, suggested, exhausted, error, onPassSelected, onShowDescription }) => {
  const isDesktop = useDesktopSize();
  const otherAccountName = useOtherAccountDisplayName(pass.accountIds);
  const remaining = calculateRemainingEntrances(pass);
  const showExpiringSoon = isValidityEndingSoon(pass);
  const hasDescription = (pass.description?.length ?? 0) > 2;
  const unlimited = isUnlimited(pass);
  const showExhausted = exhausted && !isAssigned;

  return (
    <div className={`pass-card${suggested ? " pass-card--suggested" : ""}${showExhausted ? " pass-card--exhausted" : ""}`}>
      <div className="pass-card__top">
        <div className="pass-card__title">
          <span>{pass.name}</span>
          {hasDescription &&
            (isDesktop ? (
              <HtmlTooltip
                placement="right"
                arrow={true}
                title={
                  <article className="tooltip-description" dangerouslySetInnerHTML={{ __html: pass.description }} />
                }
              >
                <BiInfoCircle className="pass-card__tooltip-icon" />
              </HtmlTooltip>
            ) : (
              <BiInfoCircle className="pass-card__tooltip-icon" onClick={() => onShowDescription(pass)} />
            ))}
        </div>
        {showExpiringSoon && <EAChip className="pass-card__expiry" label={expiryLabel(pass)} variant="warning" />}
      </div>

      <PassValidity pass={pass} />

      {otherAccountName && (
        <p className="pass-card__participant">
          Uczestnik: <strong>{otherAccountName}</strong>
        </p>
      )}

      {error && <EAAlert type="error" message={error} compact />}

      <div className="pass-card__bottom">
        <PassEntrances pass={pass} />
        <div className="pass-card__action">
          {showExhausted ? (
            <span className="pass-card__exhausted">Wyczerpano</span>
          ) : isAssigned ? (
            <HtmlTooltip placement="top" title="Kliknij, aby wypisać się z tej rezerwacji.">
              <div>
                <EAButton
                  title="Wypisz się"
                  onClick={() => onPassSelected(getId(pass), true)}
                  startIcon={<MdOutlineBackspace />}
                  variant="outlined"
                  color="secondary"
                  compact
                  disabled={isLoading}
                />
              </div>
            </HtmlTooltip>
          ) : (
            <EAButton
              title="Zapisz się"
              onClick={() => onPassSelected(getId(pass), false)}
              variant={suggested ? "filled" : "outlined"}
              compact
              disabled={isLoading || (!unlimited && remaining <= 0)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const PassSelection: React.FC<IProps> = ({
  exerciseDetails,
  onPassSelected,
  onBack,
  alert,
  loading: passLoading = false,
}) => {
  const { t } = useTranslation();
  const locale = useLocaleByLanguage();
  const { loading, getFilteredPasses } = useCourtPasses();
  const [selectedPassInfo, setSelectedPassInfo] = useState<Ticket | null>(null);
  const [attemptedPassId, setAttemptedPassId] = useState<string | null>(null);
  const passesInRange = exerciseDetails ? getFilteredPasses(exerciseDetails.startTime) : [];
  const bookablePasses = passesInRange.filter((pass) => !isPassExhausted(pass));
  const exhaustedPasses = passesInRange.filter(isPassExhausted);
  const { checking, eligibility } = usePassEligibility(bookablePasses, exerciseDetails);

  if (!exerciseDetails) {
    return <EASkeletonLoader height="200px" />;
  }

  const suggested: Ticket[] = [];
  const other: Ticket[] = [];
  if (!checking) {
    bookablePasses.forEach((pass) => {
      if (eligibility[getId(pass)] === "eligible") suggested.push(pass);
      else other.push(pass);
    });
    suggested.sort((a, b) => a.validto - b.validto);
  }

  const checkFailed = other.some((pass) => eligibility[getId(pass)] !== "not-eligible");

  const selectPass = (passId: string, assigned?: boolean) => {
    setAttemptedPassId(passId);
    onPassSelected(passId, assigned);
  };

  const errorFor = (pass: Ticket) => (alert && attemptedPassId === getId(pass) ? alert : undefined);

  const start = exerciseDetails.startTime;
  const dateLabel = format(start, "EEEE, d MMMM", { locale });
  const timeLabel = `${format(start, "HH:mm")}–${format(exerciseDetails.closeTime, "HH:mm")}`;

  const renderCard = (pass: Ticket, isSuggested: boolean, exhausted = false) => (
    <PassCard
      key={getId(pass)}
      pass={pass}
      isAssigned={exerciseDetails.assigned}
      isLoading={passLoading}
      suggested={isSuggested}
      exhausted={exhausted}
      error={errorFor(pass)}
      onPassSelected={selectPass}
      onShowDescription={setSelectedPassInfo}
    />
  );

  const renderList = () => {
    if (loading || checking) return <EASkeletonLoader height="120px" />;

    if (passesInRange.length === 0) {
      return <EAAlert type="warning" message="Nie masz dostępnych karnetów do zapisania się na te zajęcia." />;
    }

    const showRemainingHeading = suggested.length > 0 || bookablePasses.length > 0;

    return (
      <>
        {suggested.length > 0 ? (
          <section className="pass-section pass-section--suggested">
            <h3>Sugerowane</h3>
            {suggested.map((pass) => renderCard(pass, true))}
          </section>
        ) : bookablePasses.length > 0 ? (
          <div className="pass-selection__empty">
            <p>{checkFailed ? "Nie udało się sprawdzić dopasowania" : "Brak karnetu dopasowanego do tego terminu"}</p>
            <span>
              {checkFailed
                ? "Możesz zapisać się dowolnym karnetem z listy poniżej."
                : "Żaden z Twoich karnetów nie obejmuje tych zajęć."}
            </span>
          </div>
        ) : null}

        {(other.length > 0 || exhaustedPasses.length > 0) && (
          <section className="pass-section">
            {showRemainingHeading && <h3>Pozostałe</h3>}
            {other.map((pass) => renderCard(pass, false))}
            {exhaustedPasses.map((pass) => renderCard(pass, false, true))}
          </section>
        )}
      </>
    );
  };

  return (
    <div className="pass-selection">
      <div className="pass-when">
        <div className="pass-date-tile" aria-hidden="true">
          <span>{format(start, "LLL", { locale })}</span>
          <b>{format(start, "d", { locale })}</b>
          <small>{format(start, "EEE", { locale })}</small>
        </div>
        <div className="pass-when__text">
          <h2>{exerciseDetails.exerciseGroup?.displayName || exerciseDetails.name}</h2>
          <p>
            <em>{dateLabel}</em>
            <span> · {timeLabel}</span>
          </p>
        </div>
      </div>

      <div className="pass-selection__list">{renderList()}</div>

      <div className="pass-selection__footer">
        <EAButton title="Powrót" onClick={onBack} startIcon={<MdArrowBack />} variant="text" noMargins />
      </div>

      {selectedPassInfo && (
        <EAModal
          isOpen={!!selectedPassInfo}
          onClose={() => setSelectedPassInfo(null)}
          title={selectedPassInfo.name}
          hideButtons
        >
          {(selectedPassInfo.description?.length ?? 0) > 1 ? (
            <div dangerouslySetInnerHTML={{ __html: selectedPassInfo.description }} />
          ) : (
            <p>{t("brak_opisu")}</p>
          )}
        </EAModal>
      )}
    </div>
  );
};

export default PassSelection;
